import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosInstance from '../config/Axios';
import { useCustomToast } from '../pages/Contexts/ToastContext';
import { useEntitlements } from '../hooks/useEntitlements';

/**
 * Wall Groups (Phase 4) — group walls so a playlist can be assigned to all of them
 * at once and started together (fan-out; no cross-wall sync).
 *   GET/POST   /wall-groups
 *   GET/PUT/DELETE /wall-groups/{id}
 *   POST /wall-groups/{id}/walls   set members { wall_ids }
 *   POST /wall-groups/{id}/{start,stop,refresh}
 * Resource-wrapped; unwrap with res?.data ?? res.
 */
const LIST_KEY = 'wallGroups';
const itemKey = (id) => ['wallGroup', id];

// Not asked for a package that explicitly lacks video_wall (the backend would 402).
export const useGetWallGroups = () => {
  const { has } = useEntitlements();
  return useQuery(LIST_KEY, async () => {
    const res = (await axiosInstance.get('/wall-groups')).data;
    return (res?.data ?? res)?.groups ?? [];
  }, { staleTime: 15_000, enabled: has('video_wall') });
};

export const useGetWallGroup = (id, enabled = true) =>
  useQuery(itemKey(id), async () => {
    const res = (await axiosInstance.get(`/wall-groups/${id}`)).data;
    return (res?.data ?? res)?.group ?? null;
  }, { enabled: !!id && enabled, staleTime: 5_000 });

const mut = (fn, okMsg) => () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(fn, {
    onSuccess: () => { qc.invalidateQueries(LIST_KEY); if (okMsg) showToast('success', 'Wall Groups', okMsg); },
    onError: (err) => showToast('error', 'Wall Groups', err.response?.data?.message),
  });
};

export const useCreateWallGroup = mut((payload) => axiosInstance.post('/wall-groups', payload), 'Group created.');
export const useUpdateWallGroup = mut(({ id, ...p }) => axiosInstance.put(`/wall-groups/${id}`, p), 'Group updated.');
export const useDeleteWallGroup = mut((id) => axiosInstance.delete(`/wall-groups/${id}`), 'Group deleted.');

// Set membership — geometry mismatch comes back as body.status 422 (CompanyResource
// pattern, HTTP 200). Surface that as a warning and let the caller keep the dialog open.
export const useSetGroupWalls = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(({ id, wall_ids }) => axiosInstance.post(`/wall-groups/${id}/walls`, { wall_ids }), {
    onSuccess: (res) => {
      qc.invalidateQueries(LIST_KEY);
      const body = res?.data?.data ?? res?.data;
      if (body?.status === 422) showToast('warn', 'Wall Groups', body?.msg || 'Geometry mismatch.');
      else showToast('success', 'Wall Groups', 'Members updated.');
    },
    onError: (err) => showToast('error', 'Wall Groups', err.response?.data?.message),
  });
};
export const useStartGroup = mut((id) => axiosInstance.post(`/wall-groups/${id}/start`), 'Group started.');
export const useStopGroup = mut((id) => axiosInstance.post(`/wall-groups/${id}/stop`), 'Group stopped.');
export const useRefreshGroup = mut((id) => axiosInstance.post(`/wall-groups/${id}/refresh`), 'Group refreshing.');
